import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

import config from "../../config/api.config";
import { MonitorPlan } from "../../types/monitor";

import delexLogo from "./delexLogo.png";
import logoForMonitorList from "./logoForMonitorList.png";

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: require("@fontsource/roboto/files/roboto-latin-400-normal.woff"), // Regular
      fontWeight: "normal",
    },
    {
      src: require("@fontsource/roboto/files/roboto-latin-500-normal.woff"), // Bold
      fontWeight: "medium",
    },
    {
      src: require("@fontsource/roboto/files/roboto-latin-400-italic.woff"), // Italic
      fontStyle: "italic",
      fontWeight: "normal",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 48,
    fontFamily: "Roboto",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 11.25,
  },
  headerLabel: {
    color: "#8493AB",
  },
  headerValue: {
    color: "#3D5170",
  },
  wrapper: {
    flexDirection: "column",
    flexGrow: 1,
    minHeight: 0,
  },
  imageContainer: {
    alignItems: "flex-end",
  },
  mapContainer: {
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  mapImage: {
    objectFit: "contain",
  },
  image: {
    width: 108,
    height: 81,
    objectFit: "contain",
  },
  legendContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignContent: "flex-start",
    gap: 12,
    width: "50%",
  },
  legendTable: {
    flex: 1,
    borderTopWidth: 0.75,
    borderLeftWidth: 0.75,
    borderRightWidth: 0.75,
    borderBottomWidth: 0.75,
    borderColor: "#CED3DC",
  },
  legendRow: {
    flexDirection: "row",
    alignItems: "stretch",
    minHeight: 18,
    borderBottomWidth: 0.75,
    borderColor: "#CED3DC",
  },
  legendHeaderRow: {
    flexDirection: "row",
    alignItems: "stretch",
    minHeight: 18,
    borderBottomWidth: 0.75,
    borderColor: "#CED3DC",
  },
  legendNameCell: {
    flex: 1,
    borderRightWidth: 0.75,
    borderColor: "#CED3DC",
    paddingHorizontal: 4,
  },
  legendTotalCell: {
    width: 35,
    borderRightWidth: 0.75,
    borderColor: "#CED3DC",
  },
  legendTemporaryCell: {
    width: 30,
    justifyContent: "center",
  },
  legendName: {
    flex: 1,
    fontSize: 7,
    color: "#3D5170",
    padding: 5,
  },
  legendTotal: {
    width: 35,
    fontSize: 7,
    color: "#3D5170",
    padding: 5,
  },
  legendTemporary: {
    width: 30,
    fontSize: 7,
    padding: 5,
    color: "#3D5170",
  },
  legendHeader: {
    color: "#8493AB",
  },
});

const rowStyle = (index: number) =>
  index === 3
    ? { ...styles.legendRow, borderBottomWidth: 0 }
    : styles.legendRow;

type DocumentationProps = {
  monitorPlan: MonitorPlan;
  mapImage: string | null;
  mapImageWidth: number;
  mapImageHeight: number;
};

export const MonitorPlanPDF: React.FC<DocumentationProps> = ({
  monitorPlan,
  mapImage,
  mapImageWidth,
  mapImageHeight,
}) => {
  const logoUrl =
    config.companyName === "F+M Schädlingsbekämpfungs GmbH"
      ? logoForMonitorList
      : delexLogo;

  const pageHeight = 744;
  const pagePadding = 48;

  const availableHeight = pageHeight - pagePadding * 2;
  const availableWidth = 1052.25 - pagePadding * 2;

  const legendTableCount = Math.max(
    2,
    Math.ceil(monitorPlan.legend.length / 4)
  );

  const legendTableRows = Math.ceil(legendTableCount / 2);

  const headerHeight = 116 + (legendTableRows - 1) * 90;

  const maxMapHeight = availableHeight - headerHeight;

  const scale = Math.min(
    availableWidth / mapImageWidth,
    maxMapHeight / mapImageHeight
  );

  const fittedWidth = mapImageWidth * scale;
  const fittedHeight = mapImageHeight * scale;

  return (
    <Document>
      <Page size={[1052.25, 744]} style={styles.page}>
        <View style={styles.wrapper}>
          <View style={styles.header}>
            <View>
              <Text style={styles.headerTitle}>
                <Text style={styles.headerLabel}>Kunde: </Text>
                <Text style={styles.headerValue}>
                  {monitorPlan.customerName}
                </Text>
              </Text>

              <Text style={styles.headerTitle}>
                <Text style={styles.headerLabel}>Standort: </Text>
                <Text style={styles.headerValue}>{monitorPlan.address}</Text>
              </Text>
              <Text style={styles.headerTitle}>
                <Text style={styles.headerLabel}>Ebene: </Text>
                <Text style={styles.headerValue}>{monitorPlan.label}</Text>
              </Text>
            </View>

            <View style={styles.legendContainer}>
              {Array.from({
                length: Math.max(2, Math.ceil(monitorPlan.legend.length / 4)),
              }).map((_, tableIndex) => {
                const legendItems = monitorPlan.legend.slice(
                  tableIndex * 4,
                  tableIndex * 4 + 4
                );

                return (
                  <View key={tableIndex} style={styles.legendTable}>
                    <View style={styles.legendHeaderRow}>
                      <View style={styles.legendNameCell}>
                        <Text style={[styles.legendName, styles.legendHeader]}>
                          Monitortyp
                        </Text>
                      </View>

                      <View style={styles.legendTotalCell}>
                        <Text style={[styles.legendTotal, styles.legendHeader]}>
                          Anzahl
                        </Text>
                      </View>

                      <View style={styles.legendTemporaryCell}>
                        <Text
                          style={[styles.legendTemporary, styles.legendHeader]}
                        >
                          Temp.
                        </Text>
                      </View>
                    </View>

                    {legendItems.map((item, index) => (
                      <View key={item.monitorTypeId} style={rowStyle(index)}>
                        <View style={styles.legendNameCell}>
                          <Text style={styles.legendName}>
                            ({item.abbreviation}) {item.monitorType}
                          </Text>
                        </View>

                        <View style={styles.legendTotalCell}>
                          <Text style={styles.legendTotal}>{item.total}</Text>
                        </View>

                        <View style={styles.legendTemporaryCell}>
                          <Text style={styles.legendTemporary}>
                            {item.temporary}
                          </Text>
                        </View>
                      </View>
                    ))}

                    {Array.from({ length: 4 - legendItems.length }).map(
                      (_, index) => {
                        const rowIndex = legendItems.length + index;

                        return (
                          <View
                            key={`empty-${index}`}
                            style={rowStyle(rowIndex)}
                          >
                            <View style={styles.legendNameCell} />
                            <View style={styles.legendTotalCell} />
                            <View style={styles.legendTemporaryCell} />
                          </View>
                        );
                      }
                    )}
                  </View>
                );
              })}
            </View>

            <View style={styles.imageContainer}>
              <Image src={logoUrl} style={styles.image} />
            </View>
          </View>

          <View style={styles.mapContainer}>
            {mapImage && (
              <Image
                src={mapImage}
                style={{
                  ...styles.mapImage,
                  width: fittedWidth,
                  height: fittedHeight,
                }}
              />
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
};
